import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { portalNavigate, cancelPortalNavigation, isPortalMoving } from './portalNavigate';

// Buffer the actual mounted media, so THROUGH doesn't discard it and fetch a second video.
export function waitForFirstMedia(container, signal) {
  const media = container?.querySelector('video, img[data-story-media]');
  if (!media || (media.tagName === 'VIDEO' ? media.readyState >= 3 : media.complete && media.naturalWidth)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const event = media.tagName === 'VIDEO' ? 'canplay' : 'load';
    const clean = () => { media.removeEventListener(event, ready); media.removeEventListener('error', failed); signal?.removeEventListener('abort', abort); };
    const ready = () => { clean(); resolve(); };
    const failed = () => { clean(); reject(new Error('Media unavailable')); };
    const abort = () => { clean(); resolve(); };
    media.addEventListener(event, ready, { once: true }); media.addEventListener('error', failed, { once: true });
    signal?.addEventListener('abort', abort, { once: true });
  });
}

export default function PortalDoorway({ ringRect, closing = false, onOpened, onClosed, children }) {
  const [visible, setVisible] = useState(!ringRect);
  const frame = useRef(null);
  const callbacks = useRef({ onOpened, onClosed });
  callbacks.current = { onOpened, onClosed };
  const rect = useRef(ringRect);
  const entered = useRef(false);
  useEffect(() => {
    if (!rect.current) { callbacks.current.onOpened?.(); return undefined; }
    let disposed = false;
    const controller = new AbortController();
    const r = rect.current;
    // StrictMode's setup/cleanup rehearsal must not start a transition.
    queueMicrotask(async () => {
      if (disposed) return;
      // Another Portal is still moving (the last viewer closing, a page arriving): wait for it
      // rather than fail. A failed open used to close the viewer on the spot — a tap that did
      // nothing. If the lock never frees, open without the doorway.
      const waitStarted = Date.now();
      while (isPortalMoving() && Date.now() - waitStarted < 1500) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => { setTimeout(r, 40); });
        if (disposed) return;
      }
      if (isPortalMoving()) {
        entered.current = true;
        setVisible(true);
        callbacks.current.onOpened?.();
        return;
      }
      const ok = await portalNavigate({
        mode: 'viewer-open', ringSize: r.width,
        origin: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
        // A clip that won't load is NOT a refused open. The viewer has its own words for it
        // ("THAT DIDN'T LOAD", with TRY AGAIN and the Portal still readable underneath), and
        // they are worth more than a toast on the page behind saying to check your connection.
        // Swallowed, not skipped: this still waits for the media, it just opens either way.
        load: () => waitForFirstMedia(frame.current, controller.signal).catch(() => {}),
        commit: () => setVisible(true),
      });
      controller.abort();
      if (disposed) return;
      entered.current = ok;
      if (ok) callbacks.current.onOpened?.(); else callbacks.current.onClosed?.();
    });
    return () => { disposed = true; controller.abort(); if (!entered.current) cancelPortalNavigation(); };
  }, []);
  // Closing ALWAYS finishes, whatever the motion does. It used to hand the close back only on
  // `ok`: a refused job (another Portal still moving) or one cancelled after it committed left
  // the frame hidden with nobody told — the viewer looked closed, the owner still thought it was
  // open (`?reel=` still set), and tapping that ring again did nothing until a reload.
  useEffect(() => {
    if (!closing) return;
    const r = rect.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setVisible(false);
      callbacks.current.onClosed?.();
    };
    portalNavigate({
      mode: 'viewer-close', ringSize: r?.width || 64,
      origin: r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : undefined,
      commit: () => setVisible(false),
    }).then(finish, finish);
  }, [closing]);
  return createPortal(
    <div ref={frame} data-pr-viewer className="pr-overlay-root" data-pr-door={JSON.stringify(rect.current)}
      style={{ position: 'fixed', inset: 0, zIndex: 100, visibility: visible ? 'visible' : 'hidden', viewTransitionName: visible ? 'pr-viewer' : 'none' }}
      aria-hidden={!visible || undefined}>
      {children}
    </div>, document.body,
  );
}
